import {
  Form,
  Button,
  Input,
  Card,
  Col,
  InputNumber,
  Select,
  Row,
  DatePicker,
  notification,
  message
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";

import CentralBillingOnly from "./refill_programs/components/CentralBillingOnly";
import ReimbursementAddress from "./refill_programs/components/ReimbursementAddress";
import ProgramForm from "@/components/ProgramForm";
import { getManufacturerByProgram } from "@/libs/util";
import ConsentDialog from "@/components/ConsentDialog";

const needConsentPrograms = ['wilate', 'nuwiq', 'cutaquig', 'octagam', 'fibryga', 'panzyga']

const disabledFields = [
  "patient_first_name",
  "patient_last_name",
  "date_of_birth",
  "patient_gender_code",
];

const AssistanceRequestFormRefill = () => {
  const program = JSON.parse(localStorage.getItem("Program"));
  const consentDialogRef = useRef(null);
  const themeColor = localStorage.getItem("themeColor");
  const [form] = Form.useForm();
  const { assistanceRequestId } = useParams();
  const navigate = useNavigate();
  const isOctaPharma =
    getManufacturerByProgram(program.name)?.indexOf("Octapharma") !== -1;
  const [icdCodeNames, setIcdCodeNames] = useState([]);
  const [meData, setMeData] = useState({});
  const [loading, setLoading] = useState(false);
  const [notificationApi, notificationContextHolder] = notification.useNotification();

  const renderConsentDialog = needConsentPrograms.includes(program.name)

  const unitObj = {
    IU: ["alphanine", "coagadex", "koate"],
    mg: ["ryplazim"],
    milligrams: ["fibryga"],
  };
  const unit =
    Object.entries(unitObj).find(([_, values]) =>
      values.includes(program.name),
    )?.[0] || "grams";

  const loadData = async () => {
    const { data } = await axios.get("programs/icd_code_names");
    setIcdCodeNames(data);
  };

  const loadMeData = async () => {
    const { data } = await axios.get("sessions/me");
    setMeData(data);
  };

  const loadAssistanceRequestData = async () => {
    const { data } = await axios.get(
      `assistance_requests/${assistanceRequestId}`,
    );
    form.setFieldsValue(data);
    form.setFieldValue(
      "expected_dispensing_date",
      dayjs(data.expected_dispensing_date),
    );
    form.setFieldValue("date_of_birth", dayjs(data.date_of_birth));
    form.setFieldValue(
      "date_prescription_written",
      dayjs(data.date_prescription_written),
    );
    form.setFieldValue("patient_id", data.patient_id);
    form.setFieldValue("num_units", null);
    form.setFieldValue("estimated_patient_out_of_pocket", null);
    form.setFieldValue("fill_num", null);
  };

  let renderRef = useRef(false);

  useEffect(() => {
    if (renderRef.current) {
      renderRef.current = false;
      return;
    }
    loadData();
    loadMeData();
    loadAssistanceRequestData();
  }, []);

  const handleSubmitConsent = async () => {
    const values = await form.validateFields();
    try {
      setLoading(true);

      const { data } = await axios.post(`assistance_requests`, {
        ... values,
        date_of_birth: dayjs(values.date_of_birth).format('YYYY-MM-DD'),
        date_prescription_written: dayjs(values.date_prescription_written).format('YYYY-MM-DD'),
        expected_dispensing_date: dayjs(values.expected_dispensing_date).format('YYYY-MM-DD'),
        help_consent: true,
      });

      navigate(`/assistance_requests/${data.id}/show?submitted=true`);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (values) => {
    console.log("Received values of form: ", values);

    try {
      setLoading(true);

      const { data } = await axios.post(`assistance_requests`, {
        ... values,
        date_of_birth: dayjs(values.date_of_birth).format('YYYY-MM-DD'),
        date_prescription_written: dayjs(values.date_prescription_written).format('YYYY-MM-DD'),
        expected_dispensing_date: dayjs(values.expected_dispensing_date).format('YYYY-MM-DD'),
      }, {
        customError: true,
      });
      navigate(`/assistance_requests/${data.id}/show?submitted=true`);
    } catch (e) {
      const errorMessage = e?.data?.message?.split("\n")?.[0] || e?.data?.message;
      if (errorMessage?.includes("Patient must consent") && isOctaPharma) {
        if (needConsentPrograms.includes(program.name)) {
          message.error(errorMessage, 5);
          consentDialogRef.current.open();
        }
      }
      notificationApi.error({
        message: errorMessage,
        duration: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {
      notificationContextHolder
    }
    <div className="mx-auto mb-6">
      <div>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Card className="mb-6 bg-zinc-50">
            <Row gutter={20}>
              <Col span={12}>
                <Form.Item
                  label="Expected date of dispensing"
                  name="expected_dispensing_date"
                  required
                >
                  <DatePicker
                    format="MM-DD-YYYY"
                    placeholder="MM-DD-YYYY"
                    className="w-full"
                    required
                  />
                </Form.Item>
              </Col>
              {program.name !== "prolastin" && (
                <Col span={12}>
                  <Form.Item
                    name="num_units"
                    required
                    label={`Number of ${unit} for this fill`}
                  >
                    <InputNumber
                      suffix={unit === "milligrams" ? "" : unit}
                      className="w-full"
                      required
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={12}>
                <Form.Item
                  name="estimated_patient_out_of_pocket"
                  label="Estimated patient out-of-pocket amount for this fill"
                  required
                >
                  <Input prefix="$" placeholder="e.g. 175.88, 200" required />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="fill_num"
                  label="Fill number"
                  required
                  extra="The first dispensing after the prescription is written is Fill number 0, the following refill is Fill number 1, etc."
                >
                  <Select required>
                    {[...Array(53).keys()].map((num) => (
                      <Select.Option
                        key={num}
                        value={num}
                      >{`${num}th`}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="patient_id" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="program_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="setting" hidden>
              <Input />
            </Form.Item>

            <ProgramForm
              programName={program.name}
              icdCodeNames={icdCodeNames}
              meData={meData}
              disabledFields={disabledFields}
              hiddenFields={[
                "expected_dispensing_date",
                "fill_num",
                "num_units",
                "estimated_patient_out_of_pocket",
              ]}
            />
          </Card>
          <CentralBillingOnly meData={meData} />
          <ReimbursementAddress meData={meData} />

          <Form.Item
            wrapperCol={{
              offset: 8,
              span: 14,
            }}
            className="mt-5"
          >
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={loading}
              style={{
                width: "60%",
                backgroundColor: themeColor,
                height: "40px",
                fontSize: "18px",
              }}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
    {
      renderConsentDialog && <ConsentDialog ref={consentDialogRef} submit={handleSubmitConsent} />
    }
    </>
  );
};

export default AssistanceRequestFormRefill;
